<template>
  <div class="relative flex flex-col" ref="inputComponent">
    <p
      v-if="inputLabel"
      class="flex items-start font-semibold text-gray-500 dark:text-gray-400 mb-2
        w-full text-left capitalize
      "
      :class="`text-${size}`"
    >
      {{ inputLabel }}<span v-if="required" class="text-red-500"> *</span>
      <FloatingTooltip v-if="tooltip || $slots.tooltip" placement="top" class="ml-2">
        <template #button>
          <font-awesome-icon
            icon="fa-solid fa-circle-question"
            class="text-primary dark:text-primary-200 dark:hover:text-primary-100 hover:text-primary-500 dark:hover:shadow-primarydark/30 cursor-pointer transition"
          />
        </template>

        <template #tooltip>
          <!-- Prefer slot, fallback to string -->
          <slot name="tooltip">
            {{ tooltip }}
          </slot>
        </template>
      </FloatingTooltip>
    </p>
    <div class="flex items-center relative">
      <p
        v-if="isDollar && numValue && (!isNaN(numValue) || numValue === null)"
        class="absolute select-none left-2"
        :class="size === 'sm' ? 'top-2.5' : 'top-2'"
      >
        $
      </p>
      <input
        type="number"
        id="inputNumber"
        v-model="numValue"
        @keydown="keyPress"
        @keydown.enter="$emit('keydownEnter')"
        @input="numInput"
        @change="formatNum"
        @paste="handlePaste"
        @focus="$emit('inputFocus')"
        @blur="formatNum"
        class="w-full text-black rounded h-10 focus:ring-1 focus:ring-primary focus:border focus:border-primary"
        :step="step"
        :min="min"
        :max="max"
        :class="inputClasses"
        :required="required"
        :placeholder="placeholder"
        :disabled="disabled"
        ref="numInput"
      >
      <span
        v-if="units"
        class="font-semibold ml-2"
        :class="`text-${size}`"
      >
        <component
          v-for="part in displayStr(units)"
          :is="part.type ? part.type : 'span'"
          :key="part.key"
        >
          {{ part.content }}
        </component>
      </span>
      <p
        v-if="append && numValue && !isNaN(numValue)"
        ref="appended"
        id="numAppended"
        class="absolute select-none whitespace-nowrap"
        :class="size === 'sm' ? 'top-2.5' : 'top-2'"
        :style="appendPosition"
      >
        <component
          v-for="part in displayStr(append)"
          :is="part.type ? part.type : 'span'"
          :key="part.key"
        >
          {{ part.content }}
        </component>
      </p>
    </div>
    <p v-if="!valid" class="text-red-700 font-medium text-sm text-left mt-1">
      {{ validationMessage }}
    </p>
  </div>
</template>

<script>
import FloatingTooltip from "@/components/Tooltips/FloatingTooltip.vue"

export default {
  name: "NumberInput",
  components: {
    FloatingTooltip
  },
  props: {
    modelValue: null,
    inputLabel: {
      type: String,
    },
    placeholder: {
      type: String,
    },
    outlined: {
      type: Boolean,
    },
    filled: {
      type: Boolean,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    size: {
      type: String,
      default: "md",
    },
    required: {
      type: Boolean,
      default: false,
    },
    step: {
      type: String,
      required: false,
      default: "any",
    },
    min: {
      type: String,
      required: false,
    },
    max: {
      type: String,
      required: false,
    },
    isDollar: {
      type: Boolean,
      default: false,
    },
    append: {
      type: String,
      required: false,
    },
    tooltip: {
      type: String,
      required: false,
    },
    units: {
      type: String,
      required: false,
    },
    valid: {
      type: Boolean,
      required: false,
      default: true
    },
    validationMessage: {
      type: String,
      required: false
    },
    autofocus: {
      type: Boolean,
      default: false
    },
    noArrows: {
      type: Boolean,
      default: false
    },
    border: {
      type: String,
      required: false
    }
  },
  emits: ["update:modelValue", "keydownEnter", "inputFocus", "submit"],
  data() {
    return {
      numValue: null,
      keyPressed: false,
      prevValue: null,
      isMounted: false
    };
  },
  computed: {
    inputClasses() {
      return {
        "border border-gray-300 dark:border-input-borderdark": this.outlined && this.valid && !this.border,
        "border border-red-700": this.outlined && !this.valid && !this.border,
        "bg-gray-100": this.filled || this.disabled,
        "p-2 text-sm": this.size === "sm",
        "p-2": this.size === "md",
        "p-4": this.size === "lg",
        "pl-4": this.numValue && !isNaN(this.numValue) && this.isDollar,
        "pr-8": this.numTooLong,
        "noArrows": this.noArrows,
        [` border ${this.border}`]: !!this.border
      };
    },
    decimalFix() {
      if (!this.step.includes(".")) return
      return this.step.split(".")[1]?.length
    },
    inputWidth() {
      if (!this.isMounted) return
      return this.$refs.inputComponent?.querySelector("input")?.clientWidth
    },
    appendWidth() {
      if (!this.isMounted || !this.append || !this.numValue || isNaN(this.numValue)) return
      return this.$refs.appended?.clientWidth
    },
    numWidth() {
      let dollarSpace = this.isDollar ? 5 : 0
      if (!this.numValue) return 0
      let numStr = this.numValue + ""
      if (numStr.length === 1) return 30
      if (numStr.length === 2) return 40
      else if (typeof(numStr) === "string"
        && numStr?.includes(".")
      ) return 13 + numStr.length * 9.5 + dollarSpace
      else return 17 + numStr.length * 9.5 + dollarSpace
    },
    numTooLong() {
      if (!this.append || !this.appendWidth) return false
      return this.numWidth + this.appendWidth + 16 >= this.inputWidth
    },
    appendPosition() {
      if (this.numTooLong) return `right: 6px;`
      return `left: ${ this.numWidth }px;`
    },
  },
  watch: {
    modelValue(val, oldVal) {
      if (val !== oldVal) {
        if (val && this.step !== "any") {
          this.numValue = parseFloat(val).toFixed(this.decimalFix)
          this.prevValue = this.numValue
        }
        else {
          this.numValue = val
          this.prevValue = this.numValue
        }
      }
    },
  },
  async mounted() {
    setTimeout(() => this.isMounted = true, 50)
    this.numValue = this.modelValue
    this.keyPress(true)
    this.formatNum()
    if (this.autofocus) {
      await this.$nextTick()
      this.$refs.numInput.select()
    }
  },
  methods: {
    keyPress(event) {
      if (event) this.keyPressed = true
      setTimeout(() => this.keyPressed = false, 50)
      // if (parseFloat(this.numValue) === 0) this.numValue = null
    },
    numInput(event) {
      // if adjusting through arrow input
      if (!event.data && event.inputType !== "deleteContentBackward") {
        // increase or decrease by 1
        event.preventDefault()
        // console.log("event.target.value: ", event.target.value)
        const initialValue = (isNaN(this.prevValue) || this.prevValue == null) ? 0 : parseFloat(this.prevValue)
        let increasedBy1 = this.step === "any"
          ? initialValue + 1
          : (initialValue + 1).toFixed(this.decimalFix)
        // console.log("initialValue: ", initialValue)
        let decreasedBy1 = this.step === "any"
          ? initialValue - 1
          : (initialValue - 1).toFixed(this.decimalFix)
        // if no modelValue set to 0
        // console.log({ increasedBy1, decreasedBy1 })
        // console.log("this.initialValue: ", this.initialValue)
        // console.log("this.initialValue >= 0: ", this.initialValue >= 0)
        // console.log("this.initialValue < event.target.value", this.initialValue < event.target.value)
        //  console.log(initialValue > event.target.value)
        if (this.prevValue === null || this.prevValue === undefined) {
          // console.log("previously null")
          this.numValue = parseFloat(0).toFixed(this.decimalFix)
          this.$emit("update:modelValue", this.numValue)
        }
        // if higher than previous modelValue, user is clicking on up arrow -> add 1
        else if (initialValue < Number(event.target.value)) {
          // console.log("add 1!")
          // as long as not going over max
          if (!this.max || parseFloat(increasedBy1) <= parseFloat(this.max)) this.numValue = increasedBy1
          else this.numValue = parseFloat(this.max).toFixed(this.decimalFix)
        }
        // else decrease by 1 as long as no going lower than minimum
        else if (!this.min || parseFloat(decreasedBy1) > parseFloat(this.min)) {
          // console.log("minus 1!")
          this.numValue = decreasedBy1
        }
        // if hitting minimum set to minimum
        else {
          this.numValue = parseFloat(this.min).toFixed(this.decimalFix)
        }
      }
    },
    formatNum() {
      if (this.numValue == null) {
        this.$emit("update:modelValue", null)
        this.prevValue = this.numValue
        return
      }
      if (event?.target.value) {
        if (this.min && parseFloat(event.target.value) < parseFloat(this.min)) {
          this.numValue = parseFloat(this.min).toFixed(this.decimalFix)
        }
        else if (this.max && parseFloat(event.target.value) > parseFloat(this.max)) {
          this.numValue = parseFloat(this.max).toFixed(this.decimalFix)
        }
      }
      if (this.step !== "any") {
        this.numValue = parseFloat(this.numValue).toFixed(this.decimalFix)
      }
      this.$emit("update:modelValue", parseFloat(this.numValue))
      this.prevValue = this.numValue
    },
    async handlePaste() {
      navigator.clipboard
        .readText()
        .then((clipText) => {
          let pastedNum = parseFloat(clipText)
          if (isNaN(pastedNum)) return
          if (pastedNum > this.max) pastedNum = this.max
          else if (pastedNum < this.min) pastedNum = this.min
          this.numValue = pastedNum
          this.formatNum()
      })
    },
    select() {
      this.$refs.numInput.select()
    },
    displayStr(str) {
      if (!str) return
      return str.split(/(\^\w+)/g).map((part, index) => {
        if (part.startsWith('^')) {
          return { type: 'sup', content: part.slice(1), key: `sup-${index}` }
        }
        return { type: 'text', content: part, key: `text-${index}` }
      })
    },
  },
};
</script>

<style scoped>
[data-theme="dark"] #inputNumber {
  background-color: var(--cc-input);
  /* border-color: var(--cc-input-border); */
  color: var(--cc-input-text);
}



/* Chrome, Safari, Edge, Opera */
.noArrows::-webkit-outer-spin-button,
.noArrows::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
.noArrows {
  -moz-appearance: textfield;
}
</style>